import { useState } from 'react'

import { Button, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import Title from 'components/Title'

const useStyles = makeStyles((theme) => ({
  stepContainer: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'center',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    textTransform: 'none',
    minWidth: 'auto',
    '&:hover': {
      background: 'none',
      textDecoration: 'underline',
    },
  },
}))

type ValidateEmailProps = {
  onResend: () => Promise<unknown>
}

const ValidateEmail = ({ onResend }: ValidateEmailProps) => {
  const classes = useStyles()
  const [isResending, setIsResending] = useState(false)
  const [resendCounter, setResendCounter] = useState(0)
  const [resendStatus, setResendStatus] = useState<'idle' | 'sent' | 'error'>('idle')

  const handleResend = async () => {
    setIsResending(true)
    setResendStatus('idle')

    try {
      await onResend()
      setResendStatus('sent')
    } catch {
      // Re-enable the button so they can try again immediately on failure.
      setResendStatus('error')
      setIsResending(false)
      return
    }

    // wait 30 seconds before allowing another resend and set a counter to show how long until they can resend again
    setResendCounter(30)
    const interval = setInterval(() => {
      setResendCounter((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          setIsResending(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  return (
    <>
      <div className={classes.stepContainer}>
        <Title title="Check your email" />
        <Typography variant="body2">
          We’ve sent an email to the address you provided. Verify it’s you to continue.
        </Typography>
      </div>
      <div className={classes.buttonContainer}>
        <Typography color="textSecondary">Can't find the email? </Typography>
        <Button
          color="primary"
          className={classes.linkButton}
          onClick={handleResend}
          disabled={isResending}
        >
          Resend
        </Button>
      </div>
      <div className={classes.statusContainer}>
        {resendStatus === 'sent' && (
          <Typography
            color="primary"
            variant="caption"
          >
            Email sent. Please check your inbox.
          </Typography>
        )}
        {resendStatus === 'error' && (
          <Typography
            color="error"
            variant="caption"
          >
            We couldn't resend the email. Please try again.
          </Typography>
        )}
        {isResending && resendCounter > 0 && (
          <Typography
            color="textSecondary"
            variant="caption"
          >
            You can resend in {resendCounter} second{resendCounter !== 1 ? 's' : ''}
          </Typography>
        )}
      </div>
    </>
  )
}

export default ValidateEmail
